import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { getPresetPositionValues, calculateWatermarkCoordinates } from "../../utils/groupSettings/settingsHelpers";

const useWatermarkEditor = ({ initialWatermarkState }) => {
  const [selectedSamplePhotoIndex, setSelectedSamplePhotoIndex] = useState(0);
  const [wmEnabled, setWmEnabled] = useState(true);
  const [wmFile, setWmFile] = useState(null);
  const [wmPreviewUrl, setWmPreviewUrl] = useState("");
  const [wmPosition, setWmPosition] = useState("bottom-right");
  const [wmPosX, setWmPosX] = useState(85);
  const [wmPosY, setWmPosY] = useState(85);
  const [wmScale, setWmScale] = useState(20);
  const [wmOpacity, setWmOpacity] = useState(70);
  const [isWmDragging, setIsWmDragging] = useState(false);
  const [isWatermarkMoving, setIsWatermarkMoving] = useState(false);
  const wmCanvasRef = useRef(null);

  const initializedForGroupId = useRef(null);

  useEffect(() => {
    if (initialWatermarkState?.groupId && initializedForGroupId.current !== initialWatermarkState.groupId) {
      setWmEnabled(initialWatermarkState.wmEnabled);
      setWmPreviewUrl(initialWatermarkState.wmPreviewUrl);
      setWmPosition(initialWatermarkState.wmPosition);
      setWmPosX(initialWatermarkState.wmPosX);
      setWmPosY(initialWatermarkState.wmPosY);
      setWmScale(initialWatermarkState.wmScale);
      setWmOpacity(initialWatermarkState.wmOpacity);

      initializedForGroupId.current = initialWatermarkState.groupId;
    }
  }, [initialWatermarkState]);

  const handleWmFileChange = (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select a valid watermark PNG/image file");
      return;
    }
    setWmFile(file);
    setWmPreviewUrl(URL.createObjectURL(file));
  };

  const handleWmDrop = (e) => {
    e.preventDefault();
    setIsWmDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleWmFileChange(e.dataTransfer.files[0]);
    }
  };

  const updateWatermarkPosFromEvent = (e) => {
    if (!wmCanvasRef.current) return;
    const rect = wmCanvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const { x, y } = calculateWatermarkCoordinates(clientX, clientY, rect);

    setWmPosX(x);
    setWmPosY(y);
    setWmPosition("custom");
  };

  const handleWmCanvasMouseDown = (e) => {
    e.preventDefault();
    setIsWatermarkMoving(true);
    updateWatermarkPosFromEvent(e);
  };

  const handleWmCanvasMouseMove = (e) => {
    if (!isWatermarkMoving) return;
    updateWatermarkPosFromEvent(e);
  };

  const handleWmCanvasMouseUp = () => {
    setIsWatermarkMoving(false);
  };

  const applyPresetPosition = (preset) => {
    setWmPosition(preset);
    const coords = getPresetPositionValues(preset);
    if (coords) {
      setWmPosX(coords.posX);
      setWmPosY(coords.posY);
    }
  };

  return {
    wmEnabled,
    setWmEnabled,
    wmFile,
    wmPreviewUrl,
    wmPosition,
    setWmPosition,
    wmPosX,
    setWmPosX,
    wmPosY,
    setWmPosY,
    wmScale,
    setWmScale,
    wmOpacity,
    setWmOpacity,
    selectedSamplePhotoIndex,
    setSelectedSamplePhotoIndex,
    isWmDragging,
    setIsWmDragging,
    wmCanvasRef,
    handleWmFileChange,
    handleWmDrop,
    handleWmCanvasMouseDown,
    handleWmCanvasMouseMove,
    handleWmCanvasMouseUp,
    applyPresetPosition,
  };
};

export default useWatermarkEditor;
